import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';

import { Button } from '@components';
import { useContext } from '@hooks';

import { ProductCardContext } from './ProductCard';

/**
 * ProductCard.Favorite is a child component that displays a favorite button within a ProductCard component.
 * Must be used as a child component of the ProductCard.
 *
 * @component ProductCard.Favorite
 * @param {Object} props - The properties for the ProductCard.Favorite component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the ProductCard.Favorite container.
 * @param {boolean} [props.isFavorite] - Optional Flag to indicate if the product is a favorite.
 * @returns {ReactElement} The rendered ProductCard.Favorite component.
 * @example
 * <ProductCard
 *   href="#"
 *   productId={266712}
 *   isExclusive={true}
 *   isLimited={false}
 *   isPreorder={false}
 * >
 *   <ProductCard.Favorite isFavorite={true} />
 *   <ProductCard.Image src="https://thumblr.uniid.it/product/266712/fdf6817198ad.jpg" alt="Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6" />
 *   <ProductCard.Title>Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6</ProductCard.Title>
 * </ProductCard>
 */
export default function ProductCardFavorite({
  className,
  isFavorite,
  ...restProps
}) {
  useContext(
    ProductCardContext,
    'ProductCard.Favorite must be a child of ProductCard.'
  );

  return (
    <Button
      aria-label={isFavorite ? t`Add to Favorites` : t`Remove from Favorites`}
      className="absolute right-0 top-0 z-10"
      size="sm"
      variant="tertiary"
      {...restProps}
    >
      <Button.Icon>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
        >
          {isFavorite ? (
            <>
              <path fill="none" d="M0 0H24V24H0z" />
              <path d="M12.001 4.529c2.349-2.109 5.979-2.039 8.242.228 2.262 2.268 2.34 5.88.236 8.236l-8.48 8.492-8.478-8.492c-2.104-2.356-2.025-5.974.236-8.236 2.265-2.264 5.888-2.34 8.244-.228z" />
            </>
          ) : (
            <>
              <path fill="none" d="M0 0H24V24H0z" />
              <path d="M12.001 4.529c2.349-2.109 5.979-2.039 8.242.228 2.262 2.268 2.34 5.88.236 8.236l-8.48 8.492-8.478-8.492c-2.104-2.356-2.025-5.974.236-8.236 2.265-2.264 5.888-2.34 8.244-.228zm6.826 1.641c-1.5-1.502-3.92-1.563-5.49-.153l-1.335 1.198-1.336-1.197c-1.575-1.412-3.99-1.35-5.494.154-1.49 1.49-1.565 3.875-.192 5.451L12 18.654l7.02-7.03c1.374-1.577 1.299-3.959-.193-5.454z" />
            </>
          )}
        </svg>
      </Button.Icon>
    </Button>
  );
}

ProductCardFavorite.propTypes = {
  className: PropTypes.string,
  isFavorite: PropTypes.bool
};

ProductCardFavorite.defaultProps = {
  className: '',
  isFavorite: false
};
